/**
 * Recipe Ingredient Calculator Utility
 *
 * This utility calculates ingredient usage from sold recipes, handling:
 * - OMS ingredients (supplier products)
 * - Custom ingredients
 * - Nested recipes (recipes within recipes)
 * - Circular reference detection
 */

/**
 * Calculate quantity per portion for an ingredient
 * @param {Object} ingredient - The ingredient object
 * @param {Object} recipe - The recipe containing this ingredient
 * @returns {Number} - Quantity per portion
 */
export const calculateIngredientQtyPerPortion = (ingredient, recipe) => {
  try {
    if (!ingredient || !recipe) return 0

    const recipePortions = Number(recipe.portions) || 1
    if (recipePortions === 0) return 0

    const ingredientSize = Number(ingredient.size) || 0
    // Ingredient size is already saved in the currently selected ingredient UoM
    // (e.g. 100 with UoM g means 100g per recipe, not "100 source packs").
    // Keep this value as-is, then later stock-take flows can convert between row UoMs.
    const qtyPerPortion = ingredientSize / recipePortions

    // Handle invalid calculations
    if (isNaN(qtyPerPortion) || !isFinite(qtyPerPortion)) {
      console.warn(
        'Invalid quantity per portion calculation:',
        ingredient,
        recipe,
      )
      return 0
    }

    return qtyPerPortion
  } catch (error) {
    console.error(
      'Error calculating ingredient quantity per portion:',
      error,
      ingredient,
      recipe,
    )
    return 0
  }
}

/**
 * Recursively resolve nested recipes to base ingredients
 * @param {Object} recipe - The recipe to resolve
 * @param {Array} allRecipes - All available recipes (for lookup)
 * @param {Array} path - Array of recipe IDs in current path to prevent circular references
 * @param {Number} multiplier - Current multiplier for nested recipe portions
 * @returns {Array} - Flat list of base ingredients with quantities: [{ ingredientId, quantity, type, ...metadata }]
 */
export const resolveNestedRecipes = (
  recipe,
  allRecipes,
  path = [],
  multiplier = 1,
) => {
  try {
    if (!recipe || !allRecipes) return []

    const recipeId = recipe._id?.toString() || recipe.id?.toString()
    if (!recipeId) return []

    // Prevent circular references - check if this recipe is already in the current path
    if (path.includes(recipeId)) {
      console.warn(
        `Circular reference detected for recipe: ${recipe.title || recipeId}`,
      )
      return []
    }

    // Add current recipe to path
    const currentPath = [...path, recipeId]

    const baseIngredients = []

    // Process OMS ingredients (supplier products)
    if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
      recipe.ingredients.forEach(ingredient => {
        if (!ingredient.product) return

        const product = ingredient.product
        const productId =
          product._id?.toString() ||
          product.id?.toString() ||
          product.venueProductId?.toString()

        if (!productId) return

        const qtyPerPortion = calculateIngredientQtyPerPortion(
          ingredient,
          recipe,
        )
        const totalQuantity = qtyPerPortion * multiplier

        baseIngredients.push({
          ingredientId: productId,
          quantity: totalQuantity,
          // Use the product's actual type for display in stock, and keep a sourceType flag if needed
          type: product.type || 'oms',
          sourceType: 'oms',
          title: ingredient.title || product.title || '',
          sku: product.sku || '',
          UoM: ingredient.UoM || product.UoM || '',
          size: ingredient.size || product.size || 0,
          price: product.price || product.OMSPrice || 0,
          supplierTitle: product.supplierTitle || product.supplier?.title || '',
          hasVat: product.hasVat || false,
        })
      })
    }

    // Process custom ingredients
    if (recipe.customIngredients && Array.isArray(recipe.customIngredients)) {
      recipe.customIngredients.forEach(ingredient => {
        if (!ingredient.product) return

        const product = ingredient.product
        const productId = product._id?.toString() || product.id?.toString()

        if (!productId) return

        const qtyPerPortion = calculateIngredientQtyPerPortion(
          ingredient,
          recipe,
        )
        const totalQuantity = qtyPerPortion * multiplier

        baseIngredients.push({
          ingredientId: productId,
          quantity: totalQuantity,
          type: product.type || 'custom',
          sourceType: 'custom',
          title: product.title || '',
          sku: product.sku || '',
          UoM: product.UoM || '',
          size: ingredient.size || product.size || 0,
          price: product.price || 0,
          supplierTitle: '', // Custom products don't have suppliers
          hasVat: product.hasVat || false,
        })
      })
    }

    // Process nested recipes (recipeIngredients)
    if (recipe.recipeIngredients && Array.isArray(recipe.recipeIngredients)) {
      recipe.recipeIngredients.forEach(recipeIngredient => {
        const nestedRecipeId =
          recipeIngredient.recipe?._id?.toString() ||
          recipeIngredient.recipeId?.toString() ||
          recipeIngredient.recipe?.toString()

        if (!nestedRecipeId) return

        // Find the nested recipe in allRecipes
        const nestedRecipe = allRecipes.find(
          r => (r._id?.toString() || r.id?.toString()) === nestedRecipeId,
        )

        if (!nestedRecipe) {
          console.warn(`Nested recipe not found: ${nestedRecipeId}`)
          return
        }

        // Calculate multiplier for nested recipe
        // recipeIngredient.portions = how many portions of nested recipe to use
        // nestedRecipe.portions = how many portions the nested recipe makes
        const nestedRecipePortions = Number(nestedRecipe.portions) || 1
        const portionsUsed = Number(recipeIngredient.portions) || 0

        if (nestedRecipePortions === 0 || portionsUsed === 0) return

        // The multiplier for the nested recipe is: (portions used / nested recipe portions) * current multiplier
        const nestedMultiplier =
          (portionsUsed / nestedRecipePortions) * multiplier

        // Recursively resolve the nested recipe
        // Pass current path to detect circular references, but allow same recipe in different branches
        const nestedIngredients = resolveNestedRecipes(
          nestedRecipe,
          allRecipes,
          currentPath, // Pass current path to detect circular references
          nestedMultiplier,
        )

        baseIngredients.push(...nestedIngredients)
      })
    }

    return baseIngredients
  } catch (error) {
    console.error('Error resolving nested recipes:', error, recipe)
    return []
  }
}

/**
 * Aggregate ingredient usage across multiple recipes
 * @param {Array} ingredientUsageMap - Array of ingredient usage objects from multiple recipes
 * @returns {Object} - Map of ingredientId to total usage: { ingredientId: totalUsage }
 */
export const aggregateIngredientUsage = ingredientUsageArray => {
  try {
    if (!Array.isArray(ingredientUsageArray)) return {}

    const aggregated = {}

    ingredientUsageArray.forEach(ingredient => {
      if (!ingredient || !ingredient.ingredientId) return

      const ingredientId = ingredient.ingredientId.toString()
      const quantity = Number(ingredient.quantity) || 0

      if (isNaN(quantity) || !isFinite(quantity)) {
        console.warn('Invalid quantity in ingredient usage:', ingredient)
        return
      }

      if (aggregated[ingredientId]) {
        aggregated[ingredientId].quantity += quantity
      } else {
        aggregated[ingredientId] = {
          ingredientId: ingredientId,
          quantity: quantity,
          type: ingredient.type || 'oms',
          title: ingredient.title || '',
          sku: ingredient.sku || '',
          UoM: ingredient.UoM || '',
          size: ingredient.size || 0,
          price: ingredient.price || 0,
          supplierTitle: ingredient.supplierTitle || '',
          hasVat: ingredient.hasVat || false,
        }
      }
    })

    return aggregated
  } catch (error) {
    console.error('Error aggregating ingredient usage:', error)
    return {}
  }
}

/**
 * Calculate ingredient usage from generic recipe entries
 * This is shared for sold/wasted recipe flows.
 * @param {Array} recipeEntries - Array of recipe entry objects: [{ recipeId, totalPortionsSold, ... }]
 * @param {Array} allRecipes - All available recipes with full ingredient data
 * @param {Object} options - Optional config
 * @param {string} options.portionField - Field name to use for total portions
 * @returns {Object} - Map of ingredientId to total usage: { ingredientId: { ingredientId, quantity, type, ...metadata } }
 */
export const calculateIngredientUsageFromRecipes = (
  recipeEntries,
  allRecipes,
  options = {},
) => {
  try {
    if (!Array.isArray(recipeEntries) || !Array.isArray(allRecipes)) {
      console.warn('Invalid input to calculateIngredientUsageFromRecipes')
      return {}
    }

    const portionField = options.portionField || 'totalPortionsSold'

    // Filter out empty/zero quantities
    const validRecipeEntries = recipeEntries.filter(
      entry =>
        entry && (Number(entry[portionField]) || 0) > 0 && entry.recipeId,
    )

    if (validRecipeEntries.length === 0) return {}

    const allIngredientUsage = []

    // Process each recipe entry
    validRecipeEntries.forEach(recipeEntry => {
      const recipeId = recipeEntry.recipeId?.toString()
      if (!recipeId) return

      // Find the recipe in allRecipes
      const recipe = allRecipes.find(
        r => (r._id?.toString() || r.id?.toString()) === recipeId,
      )

      if (!recipe) {
        console.warn(`Recipe not found for recipe entry: ${recipeId}`)
        return
      }

      // Calculate total portions from the configured field
      const totalPortions = Number(recipeEntry[portionField]) || 0
      if (totalPortions === 0) return

      // Resolve all ingredients (including nested recipes) for this recipe
      const resolvedIngredients = resolveNestedRecipes(
        recipe,
        allRecipes,
        [],
        totalPortions,
      )

      allIngredientUsage.push(...resolvedIngredients)
    })

    // Aggregate usage across all recipes
    const aggregatedUsage = aggregateIngredientUsage(allIngredientUsage)

    return aggregatedUsage
  } catch (error) {
    console.error('Error calculating ingredient usage from recipes:', error)
    return {}
  }
}

/**
 * Backward-compatible sold recipes helper
 * @param {Array} soldRecipes - Array of sold recipe objects: [{ recipeId, recipeName, unitType, qtySold, totalPortionsSold }]
 * @param {Array} allRecipes - All available recipes with full ingredient data
 * @returns {Object} - Aggregated ingredient usage map
 */
export const calculateIngredientUsage = (soldRecipes, allRecipes) =>
  calculateIngredientUsageFromRecipes(soldRecipes, allRecipes, {
    portionField: 'totalPortionsSold',
  })
